'use client'

import React, { useEffect, useId, useState, useRef } from "react"
import { useSearchParams, usePathname, useRouter } from "next/navigation"
import { useDebounceCallback } from "usehooks-ts"
import Checkbox from "@/components/form/Checkbox"
import { changeFilters, convertsURLSearchParamsToObject, toggleFilter } from "@/services/search"
import { searchJobs } from "@/api/helper"
import { contractTypes, jobStatus, jobTitles } from "@/services/job"
import IMask from 'imask'
import { regions } from "@/services/geography"

type FiltersProps = {
    aroeven: Aroeven,
    contextSearchParams: any
}

export default function Filters({
    aroeven,
    contextSearchParams
}: FiltersProps) {

    const searchParams = useSearchParams()
    const pathname = usePathname()
    const { replace } = useRouter()
    const [facets, setFacets] = useState({})

    const refMinDate = useRef<HTMLInputElement>()
    const refMaxDate = useRef<HTMLInputElement>()

    const changeFiltersWithDebounce = useDebounceCallback((filters: any) => {
        replace(`${pathname}?${changeFilters(searchParams, filters).toString()}`, {
            scroll: false
        })
    }, 300)

    useEffect(() => {
        async function updateFacets() {
            const { facets: newFacets } = await searchJobs(aroeven, {
                ...convertsURLSearchParamsToObject(searchParams),
                ...contextSearchParams
            })
            setFacets(newFacets)
        }
        updateFacets()
    }, [searchParams])

    useEffect(() => {
        if (refMinDate.current) {
            IMask(refMinDate.current, {
                mask: "00/00/0000",
                min: new Date(1900, 0, 1),
                max: new Date(2024, 0, 1),
                lazy: false,
            })
        }

        if (refMaxDate.current) {
            IMask(refMaxDate.current, {
                mask: "00/00/0000",
                min: new Date(1900, 0, 1),
                max: new Date(2024, 0, 1),
                lazy: false,
            })
        }

    }, [searchParams])

    return (
        <>
            <aside className="sidebar filtres bg-bloc y-gap-40">
                <div className="sidebar__item">
                    <h4 className="text-18 mb-20">Contrat</h4>
                    <div className="sidebar-checkbox">
                        {Object.entries(contractTypes).map(([key, data], index) => {
                            const searchParam = 'contractType'
                            return (
                                <React.Fragment key={"checkbox-" + index}>
                                    <div className="d-flex items-center justify-content-between">
                                        <Checkbox
                                            defaultChecked={searchParams.get(searchParam)?.split(',').includes(key) ?? false}
                                            toggle={() => replace(`${pathname}?${toggleFilter(searchParams, searchParam, key).toString()}`, {
                                                scroll: false
                                            })}
                                            label={data.name}
                                        />
                                        <div className="col-auto">
                                            <div className="text-15 text-light-1">{searchParam in facets && Array.isArray(facets[searchParam]) && facets[searchParam].filter(f => f.value === key).length > 0 ? <>{facets[searchParam].filter(f => f.value === key)[0].count}</> : 0}</div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            )
                        })}
                    </div>
                </div>
                <div className="sidebar__item">
                    <h4 className="text-18 mb-20">Région d'exercice</h4>
                    <div className="sidebar-checkbox">
                        {Object.entries(regions).map(([key, data], index) => {
                            const searchParam = 'regions'
                            return (
                                <React.Fragment key={"checkbox-" + index}>
                                    <div className="d-flex items-center justify-content-between">
                                        <Checkbox
                                            defaultChecked={searchParams.get(searchParam)?.split(',').includes(key) ?? false}
                                            toggle={() => replace(`${pathname}?${toggleFilter(searchParams, searchParam, key).toString()}`, {
                                                scroll: false
                                            })}
                                            label={data.name}
                                        />
                                        <div className="col-auto">
                                            <div className="text-15 text-light-1">{searchParam in facets && Array.isArray(facets[searchParam]) && facets[searchParam].filter(f => f.value === key).length > 0 ? <>{facets[searchParam].filter(f => f.value === key)[0].count}</> : 0}</div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            )
                        })}
                    </div>
                </div>
                <div className="sidebar__item">
                    <h4 className="text-18 mb-20">Type de poste</h4>
                    <div className="sidebar-checkbox">
                        {Object.entries(jobStatus).map(([key, data], index) => {
                            const searchParam = 'jobStatus'
                            return (
                                <React.Fragment key={"checkbox-" + index}>
                                    <div className="d-flex items-center justify-content-between">
                                        <Checkbox
                                            defaultChecked={searchParams.get(searchParam)?.split(',').includes(key) ?? false}
                                            toggle={() => {                                                
                                                const updatedSearchParams = toggleFilter(searchParams, searchParam, key)
                                                if (updatedSearchParams.has('jobStatus')) {
                                                    if ([updatedSearchParams.get('jobStatus')].flat().length === 1) {
                                                        if ([updatedSearchParams.get('jobStatus')].flat()[0] === 'permanent') {
                                                            updatedSearchParams.delete('jobTitle')
                                                            // TODO retirer aussi date min date max
                                                        }
                                                    }
                                                }                                                
                                                replace(`${pathname}?${updatedSearchParams.toString()}`, {
                                                    scroll: false
                                                })
                                            }}                                            
                                            label={data.name}
                                        />
                                        <div className="col-auto">
                                            <div className="text-15 text-light-1">{searchParam in facets && Array.isArray(facets[searchParam]) && facets[searchParam].filter(f => f.value === key).length > 0 ? <>{facets[searchParam].filter(f => f.value === key)[0].count}</> : 0}</div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            )
                        })}
                    </div>
                </div>
                {(searchParams.has('jobStatus') === false || (Array.isArray(searchParams.get('jobStatus')) ? (searchParams.get('jobStatus').includes('permanent') === false) : searchParams.get('jobStatus') !== 'permanent')) &&
                    <>
                        <div className="sidebar__item">
                            <h4 className="text-18 mb-20">Date</h4>
                            <div className="form-input date mb-10">
                                <input ref={refMinDate} type="text" />
                                <label className="lh-1 text-light-1">
                                    Début à partir de :
                                </label>
                            </div>
                            <div className="form-input date">
                                <input ref={refMaxDate} type="text" />
                                <label className="lh-1 text-light-1">
                                    Fin avant le :
                                </label>
                            </div>
                        </div>
                        <div className="sidebar__item">
                            <h4 className="text-18 mb-20">Titre du poste</h4>
                            <div className="sidebar-checkbox">
                                {Object.entries(jobTitles).map(([key, data], index) => {
                                    const searchParam = 'jobTitle'
                                    return (
                                        <React.Fragment key={"checkbox-" + index}>
                                            <div className="d-flex items-center justify-content-between">
                                                <Checkbox
                                                    defaultChecked={searchParams.get(searchParam)?.split(',').includes(key) ?? false}
                                                    toggle={() => replace(`${pathname}?${toggleFilter(searchParams, searchParam, key).toString()}`, {
                                                        scroll: false
                                                    })}
                                                    label={data.name}
                                                />
                                                <div className="col-auto">
                                                    <div className="text-15 text-light-1">{searchParam in facets && Array.isArray(facets[searchParam]) && facets[searchParam].filter(f => f.value === key).length > 0 ? <>{facets[searchParam].filter(f => f.value === key)[0].count}</> : 0}</div>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    )
                                })}
                            </div>
                        </div>
                    </>
                }
                <div className="col-12 pb-0 pt-10">
                    <a href="#" onClick={(e) => {
                        e.preventDefault()
                        replace(`${pathname}`, {
                            scroll: false
                        })
                    }}>
                        <span className="info d-flex justify-content-center mt-10">
                            <i className="fi fi-ss-circle-xmark mr-5" />
                            Annuler tous les filtres
                        </span></a>
                </div>
            </aside>
        </>
    )
}